import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { delay, simulate } from '../mock-utils'
import { climateDevices, deriveClimate } from './real'
import type { ArrivalPrep, Climate, ClimatePreset, ClimateResult, ClimaService, ClimateZone } from './types'

const PREP_FIELD = 'arrivalPrep'
/** "Esta propiedad permite entre 18 y 24 °C", 22 °C por defecto (CheckIn.dc.html 2h, Bloque 8 §8i). */
const PREP: ArrivalPrep = { min: 18, max: 24, step: 1, target: 22, saved: false }

/** "Confort · 22 °C, calor" y "Eco · 19 °C, automático" (PantallaClima.dc.html). */
const PRESETS: ClimatePreset[] = [
  { id: 'confort', name: 'Confort', target: 22, mode: 'heat' },
  { id: 'eco', name: 'Eco', target: 19, mode: 'auto' },
]

/** Pasos del mando de Hotel Mirador del Puerto (PantallaClima.dc.html `static`). */
const STATIC_STEPS: Climate['steps'] = [
  { order: 1, title: 'El mando está en la mesilla', text: 'Mando blanco, junto al teléfono. El aparato está sobre la puerta del baño.', photoUrl: null, photoAlt: 'Foto del mando en la mesilla' },
  { order: 2, title: 'Enciéndelo con el botón rojo', text: 'Suena un pitido y se enciende la luz de la pantalla. Sube o baja con las flechas.', photoUrl: null, photoAlt: 'Foto del botón de encendido' },
  { order: 3, title: 'Cambia de modo con el botón «MODE»', text: 'El sol es calor y el copo es frío. Pulsa hasta ver el que quieras en la pantalla.', photoUrl: null, photoAlt: 'Foto de la pantalla del mando' },
]

const APPLY_MS = import.meta.env.MODE === 'test' ? 20 : 1400

/** Cambios aplicados en esta sesión, por reserva y zona, para que se mantengan al volver a la pantalla. */
const applied = new Map<string, Partial<ClimateZone>>()
const zoneKey = (guestCode: string, zoneId: string) => `${guestCode}:${zoneId}`

/**
 * Salón a 21 °C, objetivo 22 °C en modo calor (Inicio.dc.html fase 3), rango 18–24 °C de medio en medio grado (PantallaClima.dc.html).
 * Códigos: `clima-2zonas` (salón y dormitorio), `clima-falla` (el termostato no responde),
 * `clima-sin-avisos` (sin ventana abierta), `clima-estatico` (sin termostato, como `hotel`),
 * `error-clima`, `offline-clima`, `lento-clima` (services/mock-utils.ts). El estado sigue a la llave: con
 * `fase-during` está activo; antes de la estancia, bloqueado con "prepara tu llegada".
 */
export const climaMock: ClimaService = {
  async getArrivalPrep(stay, signal) {
    await simulate(stay.guestCode, 'clima', signal)
    if (climateDevices(stay).length === 0) return null
    const target = readLocal<number>(stay.guestCode, PREP_FIELD)
    return target === null ? PREP : { ...PREP, target, saved: true }
  },
  async setArrivalPrep(stay, target) {
    await simulate(stay.guestCode, 'clima')
    if (climateDevices(stay).length === 0) throw new ServiceError('not_supported')
    const clamped = Math.min(PREP.max, Math.max(PREP.min, target))
    writeLocal(stay.guestCode, PREP_FIELD, clamped)
    return { ...PREP, target: clamped, saved: true }
  },
  async getClimate(stay, _lang, signal) {
    await simulate(stay.guestCode, 'clima', signal)
    const code = stay.guestCode
    const base = deriveClimate(code.includes('clima-estatico') ? { ...stay, dispositives: stay.dispositives.filter((d) => d.type !== 'aire_acondicionado') } : stay)
    if (base.variant !== 'connected') {
      if (!base.available) return base
      return { ...base, steps: STATIC_STEPS, houseRule: 'Apágalo al salir de la habitación. Con el balcón abierto se apaga solo.' }
    }
    const devices = code.includes('clima-2zonas') ? [...base.zones, { id: 'D5', name: 'Dormitorio', on: false, current: null, target: null, mode: 'off' as const }] : base.zones
    const zones = devices.map((z, i): ClimateZone => ({ ...z, on: true, current: i === 0 ? 21 : 19.5, target: i === 0 ? 22 : 20, mode: 'heat', ...applied.get(zoneKey(code, z.id)) }))
    return {
      ...base,
      zones,
      range: { min: 18, max: 24, step: 0.5 },
      modes: ['heat', 'cool', 'auto', 'off'],
      supportsArrivalPrep: true,
      presets: PRESETS,
      alerts: code.includes('clima-sin-avisos') ? [] : [{ code: 'window_open', zoneId: zones[0]?.id ?? null, text: null }],
    }
  },
  async setTarget(stay, zone, target) {
    return apply(stay.guestCode, zone, { target, on: true })
  },
  async setMode(stay, zone, mode) {
    return apply(stay.guestCode, zone, { mode, on: mode !== 'off' })
  },
  async applyPreset(stay, zone, presetId) {
    const preset = PRESETS.find((p) => p.id === presetId)
    if (!preset) throw new ServiceError('not_supported')
    return apply(stay.guestCode, zone, { target: preset.target, mode: preset.mode, on: true })
  },
}

async function apply(guestCode: string, zone: ClimateZone, change: Partial<ClimateZone>): Promise<ClimateResult> {
  await simulate(guestCode, 'clima')
  await delay(APPLY_MS)
  if (guestCode.includes('clima-falla')) return { status: 'error', message: 'mock' }
  const key = zoneKey(guestCode, zone.id)
  applied.set(key, { ...applied.get(key), ...change })
  return { status: 'applied', zone: { ...zone, ...change } }
}
